"use client";

import { EIP1193DerivedWallet } from "@aptos-labs/derived-wallet-ethereum";
import { SolanaDerivedWallet } from "@aptos-labs/derived-wallet-solana";
import {
  AccountAddress,
  AccountAuthenticator,
  AnyRawTransaction,
  Ed25519PublicKey,
  InputEntryFunctionData,
  InputGenerateTransactionOptions,
  PublicKey,
} from "@aptos-labs/ts-sdk";
import { AdapterWallet, truncateAddress, useWallet } from "@aptos-labs/wallet-adapter-react";
import { setUser as setSentryUser } from "@sentry/nextjs";
import { useTranslations } from "next-intl";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { EVM_CHAINS } from "@/components/evm-chain-selector-menu";
import { toast } from "@/components/toast";
import { KEYLESS_ACCOUNT_EXPIRED_ERROR_KEY } from "@/constants";
import {
  type ChainName,
  evmChainIdToName,
  isEvmChain,
  isSupportedEvmChainId,
} from "@/constants/chains";
import { readDexSdk } from "@/contexts/dex-sdk";
import { resolveSuiAddress } from "@/contexts/sui-address";
import { useViewAs } from "@/contexts/view-as-context";
import { disconnectWcEvm } from "@/contexts/walletconnect-evm-provider";
import { disconnectWcSolana } from "@/contexts/walletconnect-solana-provider";
import { KeylessInfo, useKeylessAccounts } from "@/hooks/use-keyless-accounts";
import { useOverrideAddress } from "@/hooks/use-overide-address";
import { useOverrideWallet } from "@/hooks/use-override-wallet";
import { getAnonymousId } from "@/utils/anonymous-id";
import { appCctpNetwork } from "@/utils/cctp/v2/contracts";
import { type EIP1193ProviderLike, switchEvmNetwork } from "@/utils/evm-network-switch";
import { validateKeylessAccount } from "@/utils/keyless/keyless";
import { meshTokenStore } from "@/utils/mesh-token-storage";

export type { ChainName } from "@/constants/chains";

type WalletAccount = ReturnType<typeof useWallet>["account"];

// Account can be the wallet account or an overridden account with address, ansName, and publicKey
type AccountOverride =
  | WalletAccount
  | {
      address: string | AccountAddress;
      ansName: string | undefined;
      publicKey: string | PublicKey | Ed25519PublicKey | AccountAddress;
    }
  | undefined;

interface EvmInfo {
  chainId: number;
  address: string;
  isLoading: boolean;
}

interface DecibelWalletContextValue {
  isLoading: boolean;
  connected: boolean;
  account: AccountOverride;
  isKeyless: boolean;
  keylessInfo: KeylessInfo | undefined;
  wallet: ReturnType<typeof useWallet>["wallet"];
  network: ReturnType<typeof useWallet>["network"];
  ethAddress: string | undefined;
  evmChainId: number | undefined;
  solanaAddress: string | null | undefined;
  suiAddress: string | null | undefined;
  connect: ReturnType<typeof useWallet>["connect"];
  signIn: ReturnType<typeof useWallet>["signIn"];
  disconnect: () => void;
  wallets: ReturnType<typeof useWallet>["wallets"];
  notDetectedWallets: ReturnType<typeof useWallet>["notDetectedWallets"];
  signTransaction: (args: {
    transactionOrPayload: AnyRawTransaction;
    asFeePayer?: boolean;
  }) => Promise<{ authenticator: AccountAuthenticator }>;
  signAndSubmitTransaction: (args: {
    data: InputEntryFunctionData;
    options?: InputGenerateTransactionOptions;
  }) => Promise<{ hash: string }>;
  isPetraVault: boolean;
  accountAddress: string | undefined;
  isOverriddenAptosWallet: boolean;
  chainName: ChainName;
  hiddenWallets: readonly AdapterWallet[];
  isEVM: boolean;
  // True while an EVM wallet is connected but its chain id hasn't resolved yet.
  // `chainName` falls back to "Aptos" during this window, so consumers that key
  // off the chain (e.g. analytics) should wait until this is false.
  isEvmChainLoading: boolean;
  ownerWalletAddress: string | undefined;
}

const DecibelWalletContext = createContext<DecibelWalletContextValue | null>(null);

const ADAPTER_SAVED_WALLET_KEY = "AptosWalletName";

export function DecibelWalletProvider({ children }: PropsWithChildren) {
  const {
    connected,
    account,
    wallet,
    network,
    signIn,
    connect,
    signTransaction,
    signAndSubmitTransaction,
    disconnect,
    wallets,
    notDetectedWallets,
    isLoading,
    hiddenWallets,
  } = useWallet();
  const { overriddenAccountAddress } = useOverrideAddress();
  const { activeKeyless, disconnectKeylessAccount } = useKeylessAccounts();
  const { isViewAsActive, viewAsAddress } = useViewAs();
  const [evmInfo, setEvmInfo] = useState<EvmInfo | null>(null);
  const ethAddress = evmInfo?.address;
  const evmChainId = evmInfo?.chainId;

  const isEVM = wallet instanceof EIP1193DerivedWallet;

  const overrideWallet = useOverrideWallet();

  const solanaAddress =
    wallet instanceof SolanaDerivedWallet ? wallet.solanaWallet.publicKey?.toString() : null;

  const tToast = useTranslations("toast");
  const [hasTimedOut, setHasTimedOut] = useState(false);

  // Timeout wallet connection if loading takes too long, and clear saved wallet
  // on page close during loading so next visit starts a fresh connection
  // This is to prevent the user from being stuck in a loading state, when the wallets doesnt send rejection events
  useEffect(() => {
    if (!isLoading || connected) return;

    // This only runs if the wallet is not connected and is in loading state, so during autoConnect or manual Connect from a disconnected state
    // If the wallet isnt connected within 15 seconds, we will timeout the connection and clear the saved wallet from local storage before unload so it doesnt popups again on next visit
    const timeoutId = setTimeout(() => {
      localStorage.removeItem(ADAPTER_SAVED_WALLET_KEY);
      setHasTimedOut(true);
      toast({
        title: tToast("titles.timedOut"),
        description: tToast("summaries.walletConnectionTimedOut"),
        variant: "error",
      });
    }, 15_000);

    const handleBeforeUnload = () => {
      localStorage.removeItem(ADAPTER_SAVED_WALLET_KEY);
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isLoading, connected, tToast]);

  // Reset timed-out state if user successfully connects later
  if (connected && hasTimedOut) {
    setHasTimedOut(false);
  }

  const suiAddress = useMemo(() => resolveSuiAddress(wallet, account), [wallet, account]);

  const memoizedKeylessAccount = useMemo(() => {
    if (!activeKeyless) return undefined;
    return {
      address: activeKeyless.account.accountAddress,
      ansName: truncateAddress(activeKeyless.account.accountAddress.toString()),
      publicKey: activeKeyless.account.publicKey,
    };
  }, [activeKeyless]);

  // Track the last supported chain so we can switch back to it from unsupported chains
  const lastSupportedChainIdRef = useRef<number>(1); // default to Ethereum
  // Track whether we've already attempted to auto-switch to prevent retry loops
  const switchAttemptedRef = useRef<number | null>(null);

  if (!isEVM && evmInfo !== null) {
    setEvmInfo(null);
  }

  // Listen for ETH account and chain changes, we need to poll for network
  // Users can change this in wallet, and we need to reflect the currently selected network ideally.
  // If the user is on an unsupported EVM chain, auto-switch them to the last supported chain.
  useEffect(() => {
    if (!isEVM) {
      switchAttemptedRef.current = null;
      return;
    }

    const fetchEthInfo = async () => {
      const [[acct], chainIdHex] = await Promise.all([
        wallet.eip1193Ethers.listAccounts(),
        wallet.eip1193Provider.request({ method: "eth_chainId" }),
      ]);
      const detectedChainId = Number(chainIdHex);

      // If we are not able to fetch this, we best disconnect the wallet with toast, else it will cause unwanted behavior throughout the app
      if (!acct?.address) {
        toast({
          title: tToast("titles.error"),
          description: tToast("summaries.walletConnectionFailed"),
          variant: "error",
        });
        disconnect();
        return;
      }

      setEvmInfo({ chainId: detectedChainId, address: acct.address, isLoading: false });

      if (isSupportedEvmChainId(detectedChainId, appCctpNetwork())) {
        lastSupportedChainIdRef.current = detectedChainId;
        switchAttemptedRef.current = null;
      } else if (switchAttemptedRef.current !== detectedChainId) {
        // Auto-switch unsupported EVM chains back to the last supported chain
        switchAttemptedRef.current = detectedChainId;
        const targetChain = EVM_CHAINS.find((c) => c.chainId === lastSupportedChainIdRef.current);
        if (targetChain) {
          try {
            await switchEvmNetwork(
              wallet.eip1193Provider as unknown as EIP1193ProviderLike,
              targetChain,
            );
            return;
          } catch {
            // User rejected or switch failed — fall through
          }
        }
      }

      setEvmInfo({ chainId: detectedChainId, address: acct.address, isLoading: false });
    };

    // Fetch immediately on connect, then poll for changes
    void fetchEthInfo();
    const interval = setInterval(() => {
      void fetchEthInfo();
    }, 5_000);

    return () => clearInterval(interval);
  }, [account, wallet, isEVM, disconnect, tToast]);

  const chainName: ChainName = useMemo(() => {
    if (isEVM) {
      if (evmChainId) {
        return evmChainIdToName(evmChainId, appCctpNetwork());
      }
    }

    if (suiAddress) {
      return "Sui";
    }

    if (solanaAddress) {
      return "Solana";
    }

    return "Aptos";
  }, [isEVM, evmChainId, suiAddress, solanaAddress]);

  // Derive as a boolean primitive (not off the `evmInfo` object) so the context
  // value memo stays stable across the 5s EVM poll — `setEvmInfo` mints a new
  // object reference each tick, which would otherwise re-render every consumer.
  const isEvmChainLoading = isEVM && evmInfo === null;

  const accountOverride = useMemo(() => {
    // View-as mode: impersonate another user (dev/preview only)
    if (isViewAsActive && viewAsAddress) {
      return {
        address: AccountAddress.fromString(viewAsAddress),
        ansName: `Viewing ${truncateAddress(viewAsAddress)}`,
        publicKey: AccountAddress.fromString(viewAsAddress),
      };
    }

    // override from query params
    if (overrideWallet) {
      return {
        address: overrideWallet.accountAddress,
        ansName: "Overridden Wallet",
        publicKey: overrideWallet.publicKey,
      };
    }

    // override from query params
    if (overriddenAccountAddress) {
      return {
        address: overriddenAccountAddress,
        ansName: "Overridden Account",
        publicKey: overriddenAccountAddress,
      };
    }

    // Keyless account
    if (memoizedKeylessAccount) {
      return memoizedKeylessAccount;
    }

    // SUI wallet
    if (suiAddress && account) {
      return {
        address: account.address,
        ansName: truncateAddress(suiAddress),
        publicKey: account.publicKey,
      };
    }

    // Solana wallet
    if (solanaAddress && account) {
      return {
        address: account.address,
        ansName: truncateAddress(solanaAddress),
        publicKey: account.publicKey,
      };
    }

    // Eth wallet
    if (ethAddress && account) {
      return {
        address: account.address,
        ansName: truncateAddress(ethAddress),
        publicKey: account.publicKey,
      };
    }

    return account;
  }, [
    account,
    ethAddress,
    suiAddress,
    memoizedKeylessAccount,
    overriddenAccountAddress,
    solanaAddress,
    overrideWallet,
    isViewAsActive,
    viewAsAddress,
  ]);

  // Petra Vault smart wallet accounts have an all-zeros public key
  const accountPublicKey = account?.publicKey;
  const isPetraVault = useMemo(() => {
    if (!accountPublicKey) return false;
    const pkStr =
      typeof accountPublicKey === "string" ? accountPublicKey : accountPublicKey.toString();
    return /^(0x)?0+$/.test(pkStr);
  }, [accountPublicKey]);

  const handleSignAndSubmitTransaction = useCallback(
    async (args: {
      data: InputEntryFunctionData;
      options?: InputGenerateTransactionOptions;
    }): Promise<{ hash: string }> => {
      const response = await signAndSubmitTransaction({
        data: args.data,
        options: args.options,
      });
      return response;
    },
    [signAndSubmitTransaction],
  );

  const handleSignTransaction = useCallback(
    async (args: { transactionOrPayload: AnyRawTransaction; asFeePayer?: boolean }) => {
      if (overrideWallet) {
        const authenticator = readDexSdk.deps.aptos.sign({
          signer: overrideWallet,
          transaction: args.transactionOrPayload,
        });
        return { authenticator };
      }

      if (activeKeyless) {
        const validatedKeylessAccount = validateKeylessAccount(activeKeyless.account);
        if (!validatedKeylessAccount) {
          throw new Error(KEYLESS_ACCOUNT_EXPIRED_ERROR_KEY);
        }
        return {
          authenticator: activeKeyless.account.signTransactionWithAuthenticator(
            args.transactionOrPayload,
          ),
        };
      }

      return signTransaction(args);
    },
    [overrideWallet, activeKeyless, signTransaction],
  );

  const handleDisconnect = useCallback(() => {
    const addr = accountOverride?.address.toString();
    disconnectWcEvm();
    disconnectWcSolana();
    disconnect();
    if (activeKeyless) {
      disconnectKeylessAccount();
    }
    if (addr) meshTokenStore(addr).clear();
  }, [disconnect, activeKeyless, disconnectKeylessAccount, accountOverride]);

  const isKeylessConnected = activeKeyless != null;

  const rawAddress = accountOverride?.address;
  // Aptos addresses have short and long forms; always send the long form to
  // Sentry so the same wallet doesn't split into two user.ids.
  const normalizedAddress =
    rawAddress instanceof AccountAddress ? rawAddress.toStringLong() : rawAddress;
  // Skip setUser during view-as: the dev's real errors would get attributed to
  // the impersonated wallet, polluting per-user dashboards and replays.
  const sentryUserId = !isViewAsActive ? normalizedAddress : undefined;
  useEffect(() => {
    // Fall back to the anonymous id rather than clearing the user: an unset
    // user makes Sentry bucket all logged-out traffic together, so one bug
    // reads as "30k events, 2 users".
    setSentryUser({ id: sentryUserId ?? getAnonymousId() });
    return () => {
      setSentryUser({ id: getAnonymousId() });
    };
  }, [sentryUserId]);

  const value: DecibelWalletContextValue = useMemo(
    () => ({
      isLoading: hasTimedOut ? false : isLoading,
      connected:
        isViewAsActive ||
        isKeylessConnected ||
        !!overrideWallet ||
        (hasTimedOut ? false : connected && !!account),
      account: accountOverride,
      isKeyless: isKeylessConnected,
      keylessInfo: activeKeyless
        ? {
            email: activeKeyless.email,
            provider: activeKeyless.provider,
          }
        : undefined,
      wallet,
      network,
      ethAddress,
      evmChainId,
      solanaAddress,
      suiAddress,
      connect,
      signIn,
      disconnect: handleDisconnect,
      wallets,
      notDetectedWallets,
      signTransaction: handleSignTransaction,
      signAndSubmitTransaction: handleSignAndSubmitTransaction,
      isPetraVault,
      accountAddress: accountOverride?.address.toString(),
      isOverriddenAptosWallet: !!overrideWallet,
      chainName,
      hiddenWallets,
      ownerWalletAddress: getOwnerWalletAddress(
        chainName,
        accountOverride?.address.toString(),
        ethAddress,
        solanaAddress,
        suiAddress,
      ),
      isEVM,
      isEvmChainLoading,
    }),
    [
      hasTimedOut,
      isLoading,
      isViewAsActive,
      isKeylessConnected,
      overrideWallet,
      connected,
      account,
      accountOverride,
      activeKeyless,
      wallet,
      network,
      ethAddress,
      evmChainId,
      isEvmChainLoading,
      solanaAddress,
      suiAddress,
      connect,
      signIn,
      handleDisconnect,
      wallets,
      notDetectedWallets,
      handleSignTransaction,
      handleSignAndSubmitTransaction,
      isPetraVault,
      chainName,
      hiddenWallets,
      isEVM,
    ],
  );

  return <DecibelWalletContext.Provider value={value}>{children}</DecibelWalletContext.Provider>;
}

export function useDecibelWallet() {
  const context = useContext(DecibelWalletContext);
  if (!context) {
    throw new Error("useDecibelWallet must be used within DecibelWalletProvider");
  }
  return context;
}

const getOwnerWalletAddress = (
  chainName: ChainName,
  accountAddress?: string,
  ethAddress?: string,
  solanaAddress?: string | null,
  suiAddress?: string,
): string | undefined => {
  if (isEvmChain(chainName)) return ethAddress;
  switch (chainName) {
    case "Solana":
      return solanaAddress || undefined;
    case "Sui":
      return suiAddress;
    case "Aptos":
      return accountAddress;
    default:
      return accountAddress;
  }
};
